import * as React from 'react';
import { flushSync } from 'react-dom';
import { CacheLookupPolicy, InteractionRequiredAuthError, InteractionStatus, type AccountInfo } from '@azure/msal-browser';
import { useAccount, useIsAuthenticated, useMsal } from '@azure/msal-react';
import {
  captureSession, isSessionBlocked, registerSessionRetirement, sessionGeneration,
  setAuthTokenProvider, setManagerGraphTokenProvider, setGroupGraphTokenProvider
} from '@cdf/api-client-react';
import {
  buildGraphTokenRequest, type GraphAccess, buildLoginRequest, buildTokenRequest,
  getApiScopes, getPostLogoutRedirectUri, isAuthConfigured
} from '@/lib/auth-config';
import { isInIframe, openTopLevelAuth } from '@/lib/auth-navigation';
import { setGraphTokenProvider } from '@/lib/graph-client';
import { useAuthMode } from '@/components/auth/auth-mode';
import { acquireApiTokenSilently } from '@/lib/api-token-recovery';
import { createGroupAccessController } from '@/lib/group-access-recovery';
import { beginExplicitSignIn, failExplicitSignIn, panelSignInHandoff, retainedLogoutAccount, retireLocalSession, useLocalSession } from '@/lib/local-session';
import { clearViewAsRole } from '@/lib/role-view';

export interface AuthContextValue {
  enabled: boolean;
  ready: boolean;
  isAuthenticated: boolean;
  account: { name: string; email: string | null } | null;
  accountKey: string | null;
  localSignedOut?: boolean;
  signOutError?: string | null;
  login: () => void;
  reconnect: () => void;
  interactionError: string | null;
  tokenError: boolean;
  logout: () => void;
  getToken: () => Promise<string | null>;
  getGraphToken: (access: GraphAccess) => Promise<string | null>;
  requestGraphConsent: (access: GraphAccess) => void;
}

const DISABLED_AUTH: AuthContextValue = {
  enabled: false, ready: true, isAuthenticated: false, account: null, accountKey: null,
  login: () => undefined, reconnect: () => undefined, interactionError: null, tokenError: false,
  logout: () => undefined, getToken: async () => null, getGraphToken: async () => null,
  requestGraphConsent: () => undefined
};
const AuthContext = React.createContext<AuthContextValue>(DISABLED_AUTH);

function useMsalAuthValue(): AuthContextValue {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const activeAccount = useAccount();
  const local = useLocalSession();
  const ready = inProgress === InteractionStatus.None;
  const iframe = React.useMemo(() => isInIframe(), []);
  const interactionPending = React.useRef(false);
  const logoutAccount = React.useRef<AccountInfo | undefined>(retainedLogoutAccount());
  const groupController = React.useRef<ReturnType<typeof createGroupAccessController> | null>(null);
  const readyRef = React.useRef(ready);
  React.useLayoutEffect(() => { readyRef.current = ready; }, [ready]);
  const [interactionError, setInteractionError] = React.useState<string | null>(null);
  const [signOutError, setSignOutError] = React.useState<string | null>(null);
  const [tokenError, setTokenError] = React.useState(false);

  const login = React.useCallback(() => {
    if (inProgress !== InteractionStatus.None || interactionPending.current) return;
    interactionPending.current = true;
    setInteractionError(null);
    const attempt = beginExplicitSignIn();
    if (iframe) {
      try { openTopLevelAuth('login', undefined, undefined, panelSignInHandoff(attempt)); }
      catch {
        failExplicitSignIn(attempt);
        setInteractionError('Sign-in could not start. Please try again.');
      }
      interactionPending.current = false;
      return;
    }
    // Redirect navigation stays on the registered root; state is verified by MSAL.
    try {
      void instance.loginRedirect({ ...buildLoginRequest(window.location.href), state: attempt, prompt: 'login' })
        .catch(() => {
          failExplicitSignIn(attempt);
          interactionPending.current = false;
          setInteractionError('Sign-in could not start. Please try again.');
        });
    } catch {
      failExplicitSignIn(attempt);
      interactionPending.current = false;
      setInteractionError('Sign-in could not start. Please try again.');
    }
  }, [instance, inProgress, iframe]);

  const logout = React.useCallback(() => {
    if (!isSessionBlocked()) logoutAccount.current = instance.getActiveAccount() ?? instance.getAllAccounts()[0] ?? undefined;
    const account = logoutAccount.current ?? retainedLogoutAccount();
    // Always retire access before interaction checks or provider work.
    flushSync(() => retireLocalSession(account));
    const generation = sessionGeneration();
    interactionPending.current = false;
    setSignOutError(null);
    setTokenError(false);
    setInteractionError(null);
    clearViewAsRole();
    setAuthTokenProvider(null);
    setGraphTokenProvider(null);
    setManagerGraphTokenProvider(null);
    setGroupGraphTokenProvider(null);
    groupController.current?.dispose();
    const failed = () => {
      if (isSessionBlocked() && sessionGeneration() === generation) {
        setSignOutError('Microsoft sign-out could not finish. Allow a new tab, then retry sign-out.');
      }
    };
    if (inProgress !== InteractionStatus.None) {
      setSignOutError('Microsoft is busy. You are signed out here; retry sign-out when it is available.');
    } else if (iframe) {
      // Remains on the user gesture. Even a null noopener handle is unconfirmed.
      try { openTopLevelAuth('logout', undefined, account); } catch { failed(); }
    } else if (!interactionPending.current && account) {
      interactionPending.current = true;
      try {
        const redirect = instance.logoutRedirect({ account, postLogoutRedirectUri: getPostLogoutRedirectUri() });
        queueMicrotask(() => { interactionPending.current = false; });
        void redirect.catch(() => { interactionPending.current = false; failed(); });
      } catch { interactionPending.current = false; failed(); }
    }
    if (account) {
      try { void instance.clearCache({ account }).catch(failed); } catch { failed(); }
    }
  }, [instance, inProgress, iframe]);

  const getToken = React.useCallback(async (): Promise<string | null> => {
    if (inProgress !== InteractionStatus.None || isSessionBlocked() || local.generation !== sessionGeneration()) return null;
    const session = captureSession();
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) return null;
    if (!getApiScopes()) { setTokenError(true); return null; }
    const token = await acquireApiTokenSilently(() => instance.acquireTokenSilent({
      ...buildTokenRequest(account), cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken
    }), value => {
      try { session.assertCurrent(); setTokenError(value); } catch { /* Retired callback. */ }
    });
    try {
      session.assertCurrent();
      const current = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      return current?.homeAccountId === account.homeAccountId && current.tenantId === account.tenantId &&
        current.localAccountId === account.localAccountId ? token : null;
    } catch { return null; }
  }, [instance, inProgress, local.generation]);

  const reconnect = React.useCallback(() => {
    if (isSessionBlocked()) { login(); return; }
    if (inProgress !== InteractionStatus.None || interactionPending.current) return;
    if (!getApiScopes()) {
      setInteractionError('Application access is not configured. Contact your administrator.');
      return;
    }
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) { login(); return; }
    interactionPending.current = true;
    setInteractionError(null);
    if (iframe) {
      openTopLevelAuth('acquireToken');
      interactionPending.current = false;
      return;
    }
    void instance.acquireTokenRedirect(buildTokenRequest(account, window.location.href)).catch(() => {
      interactionPending.current = false;
      setInteractionError('Application access could not be renewed. Please try again or contact your administrator.');
    });
  }, [instance, inProgress, iframe, login]);

  const requestGraphConsent = React.useCallback((access: GraphAccess) => {
    if (isSessionBlocked() || local.generation !== sessionGeneration()) return;
    if (inProgress !== InteractionStatus.None || interactionPending.current) return;
    interactionPending.current = true;
    if (iframe) {
      interactionPending.current = false;
      setInteractionError(null);
      const opened = openTopLevelAuth(access === 'places' ? 'acquireGraphPlaces'
        : access === 'presence' ? 'acquireGraphPresence'
        : access === 'directory' ? 'acquireGraphDirectory'
        : access === 'managerReports' ? 'acquireGraphManagerReports'
        : access === 'groups' ? 'acquireGraphGroups' : 'acquireGraphCalendar');
      if (!opened) setInteractionError('Allow a new tab for Microsoft consent, then try again.');
      return;
    }
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) {
      void instance.loginRedirect(buildLoginRequest(window.location.href)).catch(() => {
        interactionPending.current = false;
        setInteractionError('Sign-in could not start. Please try again.');
      });
      return;
    }
    void instance.acquireTokenRedirect(buildGraphTokenRequest(account, window.location.href, access)).catch(() => {
      interactionPending.current = false;
      setInteractionError('Microsoft Graph consent could not start. Please try again.');
    });
  }, [instance, inProgress, iframe, local.generation]);

  const getGraphToken = React.useCallback(async (access: GraphAccess): Promise<string | null> => {
    if (inProgress !== InteractionStatus.None || isSessionBlocked() || local.generation !== sessionGeneration()) return null;
    const session = captureSession();
    const account = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
    if (!account) return null;
    try {
      const result = await instance.acquireTokenSilent({
        ...buildGraphTokenRequest(account, undefined, access),
        cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken
      });
      session.assertCurrent();
      const current = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      if (current?.homeAccountId !== account.homeAccountId || current.tenantId !== account.tenantId ||
        current.localAccountId !== account.localAccountId) return null;
      return result.accessToken || null;
    } catch (err) {
      if (!(err instanceof InteractionRequiredAuthError) && !isSessionBlocked()) {
        console.error('Microsoft access could not be acquired.');
      }
      return null;
    }
  }, [instance, inProgress, local.generation]);

  React.useLayoutEffect(() => {
    setAuthTokenProvider(getToken);
    setGraphTokenProvider(getGraphToken);
    setManagerGraphTokenProvider(() => getGraphToken('managerReports'));
    return () => {
      setAuthTokenProvider(null);
      setGraphTokenProvider(null);
      setManagerGraphTokenProvider(null);
    };
  }, [getToken, getGraphToken, activeAccount?.homeAccountId, activeAccount?.tenantId]);

  React.useLayoutEffect(() => {
    const identity = () => {
      if (isSessionBlocked() || local.generation !== sessionGeneration()) return null;
      const current = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      return current ? [current.homeAccountId, current.tenantId, current.localAccountId].join(':') : null;
    };
    const controller = createGroupAccessController(identity, async () => {
      if (!identity()) return null;
      const session = captureSession();
      const current = instance.getActiveAccount() ?? instance.getAllAccounts()[0];
      if (!current) return null;
      const result = await instance.acquireTokenSilent({
        ...buildGraphTokenRequest(current, undefined, 'groups'),
        cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken
      });
      session.assertCurrent();
      return result.accessToken || null;
    });
    groupController.current = controller;
    setGroupGraphTokenProvider(() => readyRef.current ? controller.token() : Promise.resolve(null), identity);
    return () => {
      setGroupGraphTokenProvider(null);
      controller.dispose();
      if (groupController.current === controller) groupController.current = null;
    };
  }, [instance, local.generation, activeAccount?.homeAccountId, activeAccount?.tenantId, activeAccount?.localAccountId]);

  React.useLayoutEffect(() => registerSessionRetirement(() => {
    groupController.current?.dispose();
    setGroupGraphTokenProvider(null);
    setManagerGraphTokenProvider(null);
    setGraphTokenProvider(null);
    setAuthTokenProvider(null);
    clearViewAsRole();
  }), []);

  // A successful new generation can select another account; never retain the
  // previous logout target across that verified sign-in.
  React.useEffect(() => {
    if (!local.signedOut) { logoutAccount.current = undefined; interactionPending.current = false; }
  }, [local.generation, local.signedOut]);

  const account = !local.signedOut && activeAccount
    ? { name: activeAccount.name ?? activeAccount.username, email: activeAccount.username || null } : null;
  return {
    enabled: true, ready: local.signedOut || ready, localSignedOut: local.signedOut, signOutError,
    isAuthenticated: !local.signedOut && isAuthenticated, account,
    accountKey: !local.signedOut && activeAccount
      ? [activeAccount.homeAccountId, activeAccount.tenantId, activeAccount.localAccountId].join(':') : null,
    login, reconnect, interactionError, tokenError, logout, getToken, getGraphToken, requestGraphConsent
  };
}

function MsalAuthProvider({ children }: { children: React.ReactNode }) {
  const value = useMsalAuthValue();
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { enabled } = useAuthMode();
  if (!enabled || !isAuthConfigured()) {
    return <AuthContext.Provider value={DISABLED_AUTH}>{children}</AuthContext.Provider>;
  }
  return <MsalAuthProvider>{children}</MsalAuthProvider>;
}
export function useAuth(): AuthContextValue { return React.useContext(AuthContext); }
